/**
 * SimpleDocumentLibrary
 *
 * Simplified document browser for Initial Review.
 * Shows all documents with search (including reference search for phone numbers,
 * addresses, etc.), consistent with Enterprise Compact design.
 */
import { Search } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { useListArtifactsQuery } from '@/services/api/artifactService';
import { ArtifactType } from '@/services/types/artifact-digest-types';
import type { ArtifactUnpacked } from '@/services/types/artifact-types';

import { DocumentRowWithReferences } from './DocumentRowWithReferences';

interface SimpleDocumentLibraryProps {
  matterId: string;
  onDocumentClick?: (artifactId: string) => void;
}

/** Type guard to check if artifact is a blob type */
const isBlobArtifact = (
  artifact: ArtifactUnpacked,
): artifact is ArtifactUnpacked & {
  artifact_type: typeof ArtifactType.BLOB;
  blob: { name?: string | null; file_mime_type?: string };
} =>
  artifact.artifact_type === ArtifactType.BLOB &&
  artifact.blob !== null &&
  artifact.blob !== undefined;

export const SimpleDocumentLibrary = ({
  matterId,
  onDocumentClick,
}: SimpleDocumentLibraryProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  // Track which documents are visible during search
  const [visibleDocs, setVisibleDocs] = useState<Set<string>>(new Set());

  const {
    data: artifacts = [],
    isLoading,
    isError,
  } = useListArtifactsQuery({ matterId }, { skip: !matterId });

  // Handle visibility changes from child rows
  const handleVisibilityChange = useCallback(
    (artifactId: string, isVisible: boolean) => {
      setVisibleDocs((prev) => {
        const next = new Set(prev);
        if (isVisible) {
          next.add(artifactId);
        } else {
          next.delete(artifactId);
        }
        return next;
      });
    },
    [],
  );

  // Reset visibility tracking when search term changes
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setVisibleDocs(new Set());
  }, []);

  // Sort by modified date (newest first)
  const sortedArtifacts = useMemo(() => {
    return [...artifacts].sort(
      (a, b) =>
        new Date(b.modified_at).getTime() - new Date(a.modified_at).getTime(),
    );
  }, [artifacts]);

  // Check if an artifact's title/name matches the search term
  const checkTitleMatch = (
    artifact: ArtifactUnpacked,
    term: string,
  ): boolean => {
    if (!term) return false;

    // Check blob name
    if (isBlobArtifact(artifact)) {
      const blobName = artifact.blob.name || '';
      if (blobName.toLowerCase().includes(term)) return true;
    }

    // Check digest title
    const title = artifact.artifact_digest?.title || '';
    if (title.toLowerCase().includes(term)) return true;

    // Check one-line summary
    const summary = artifact.artifact_digest?.one_line_summary || '';
    if (summary.toLowerCase().includes(term)) return true;

    return false;
  };

  // Get display name for artifact
  const getDisplayName = (artifact: ArtifactUnpacked): string => {
    // 1) Blob: prefer uploaded file name
    if (isBlobArtifact(artifact)) {
      const blobName = artifact.blob.name || null;
      if (blobName) return blobName;
    }

    // 2) Prefer digest title
    const title = artifact.artifact_digest?.title || null;
    if (title) return title;

    // 3) One-line summary
    const summary = artifact.artifact_digest?.one_line_summary || null;
    if (summary) return summary;

    // 4) Fallback by type
    const modified = new Date(artifact.modified_at);
    const dateStr = Number.isNaN(modified.getTime())
      ? ''
      : ` • ${modified.toLocaleDateString('en-US', {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        })}`;

    switch (artifact.artifact_type) {
      case ArtifactType.TRANSCRIPT:
        return `Transcript${dateStr}`;
      case ArtifactType.AUDIO_CONVERSATION:
        return `Audio Conversation${dateStr}`;
      case ArtifactType.CONVERSATION:
        return `Conversation${dateStr}`;
      case ArtifactType.STAFF_NOTE:
        return `Staff Note${dateStr}`;
      case ArtifactType.BLOB:
      default:
        return `Document${dateStr}`;
    }
  };

  // Get if blob is an image
  const isImageBlob = (artifact: ArtifactUnpacked): boolean => {
    if (!isBlobArtifact(artifact)) return false;
    const fileMime = artifact.blob.file_mime_type || '';
    return fileMime.startsWith('image/');
  };

  return (
    <div className="space-y-2">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400 dark:text-zinc-500" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search documents..."
          className="w-full rounded border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 pl-8 pr-3 py-2 text-xs font-medium text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:border-zinc-400 dark:focus:border-zinc-600 focus:outline-none transition-colors"
        />
      </div>

      {/* Document List */}
      <div className="max-h-[400px] overflow-y-auto">
        {isLoading && (
          <div className="flex items-center gap-2 px-2 py-3">
            <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-zinc-300 dark:border-zinc-600 border-t-zinc-600 dark:border-t-zinc-400" />
            <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
              Loading documents...
            </span>
          </div>
        )}

        {isError && (
          <div className="px-2 py-3 text-xs font-medium text-red-600 dark:text-red-400">
            Unable to load documents
          </div>
        )}

        {!isLoading && !isError && sortedArtifacts.length === 0 && (
          <div className="px-2 py-3 text-xs font-medium text-zinc-500 dark:text-zinc-400">
            No documents available
          </div>
        )}

        {!isLoading &&
          !isError &&
          sortedArtifacts.length > 0 &&
          sortedArtifacts.map((artifact) => {
            const displayName = getDisplayName(artifact);
            const isImage = isImageBlob(artifact);
            const term = searchTerm.trim().toLowerCase();
            const titleMatches = checkTitleMatch(artifact, term);

            return (
              <DocumentRowWithReferences
                key={artifact.id}
                artifactId={artifact.id}
                displayName={displayName}
                artifactType={artifact.artifact_type}
                isImage={isImage}
                searchTerm={searchTerm}
                titleMatches={titleMatches}
                onClick={() => onDocumentClick?.(artifact.id)}
                onVisibilityChange={handleVisibilityChange}
              />
            );
          })}

        {/* Empty state when search returns no results */}
        {!isLoading &&
          !isError &&
          sortedArtifacts.length > 0 &&
          searchTerm.trim() &&
          visibleDocs.size === 0 && (
            <div className="px-2 py-4 text-center">
              <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                No documents match &ldquo;{searchTerm.trim()}&rdquo;
              </p>
            </div>
          )}
      </div>
    </div>
  );
};
